import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { RestaurantPublicDTO } from "../dto/restaurant.dto";

const RESTAURANT_CACHE_TTL = 60 * 1000; // 1 minuto

export const useRestaurantMetadata = (slug: string) => {
  const [data, setData] = useState<RestaurantPublicDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const cacheKey = `restaurant_cache_${slug}`;

    const readCache = (): { data: RestaurantPublicDTO; isFresh: boolean } | null => {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.data?.id) return null;
        const isFresh = Date.now() - (parsed.timestamp ?? 0) < RESTAURANT_CACHE_TTL;
        return { data: parsed.data, isFresh };
      } catch {
        localStorage.removeItem(cacheKey);
        return null;
      }
    };

    const writeCache = (value: RestaurantPublicDTO) => {
      localStorage.setItem(cacheKey, JSON.stringify({ data: value, timestamp: Date.now() }));
    };

    const fetchMetadata = async () => {
      setError(null);

      const cached = readCache();

      // Pintamos cache inmediatamente si existe
      if (cached) {
        setData(cached.data);
        if (cached.isFresh) {
          setLoading(false);
          return; // no hace falta refetch
        }
      } else {
        setLoading(true);
      }

      try {
        const { data: res, error: rpcError } = await supabase.rpc(
          "get_restaurant_public_data",
          { p_slug: slug }
        );

        const raw = Array.isArray(res) ? res[0] : res;

        if (rpcError || !raw?.id) {
          if (!cached) {
            setError("Restaurante no encontrado");
            setData(null);
          }
          return;
        }

        writeCache(raw);
        setData(raw);
      } catch {
        if (!cached) {
          setError("Error al cargar los datos del restaurante");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();

    // Revalidar al volver a la pestaña
    const onFocus = () => fetchMetadata();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [slug]);

  return { data, loading, error };
};
